import { useEffect, useRef } from "react";
import type { RefObject } from "react";
import { REST_POSE } from "../utils";
import type { HeadPose } from "../utils";

const IDLE_POSES: HeadPose[] = [
  REST_POSE,
  { yaw: -0.55, pitch: 0.3, roll: -0.08 },
  { yaw: 0.6, pitch: 0.02, roll: 0.06 },
  { yaw: 0.1, pitch: -0.32, roll: 0 },
  { yaw: -0.32, pitch: -0.1, roll: -0.04 },
  { yaw: 0.35, pitch: 0.24, roll: 0.05 },
];
const EASE_PER_SECOND = 5;

export function useHeadMotion(
  ref: RefObject<Element | null>,
  seed: number,
  onPose: (pose: HeadPose) => void,
) {
  const draw = useRef(onPose);

  useEffect(() => {
    draw.current = onPose;
  });

  useEffect(() => {
    const element = ref.current;
    if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let pose = REST_POSE;
    let target = REST_POSE;
    let poseIndex = seed % IDLE_POSES.length;
    let nextIdleAt = performance.now() + 1200 + (seed % 7) * 250;
    let frame = 0;
    let last = 0;

    function tick(now: number) {
      const seconds = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (now > nextIdleAt) {
        poseIndex = (poseIndex + 1 + ((seed + poseIndex) % 2)) % IDLE_POSES.length;
        target = IDLE_POSES[poseIndex];
        nextIdleAt = now + 1600 + ((seed + poseIndex * 3) % 5) * 350;
      }
      const k = 1 - Math.exp(-seconds * EASE_PER_SECOND);
      pose = {
        yaw: pose.yaw + (target.yaw - pose.yaw) * k,
        pitch: pose.pitch + (target.pitch - pose.pitch) * k,
        roll: pose.roll + (target.roll - pose.roll) * k,
      };
      draw.current(pose);
      frame = requestAnimationFrame(tick);
    }

    function start() {
      if (frame) return;
      last = performance.now();
      frame = requestAnimationFrame(tick);
    }

    function stop() {
      cancelAnimationFrame(frame);
      frame = 0;
    }

    const observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()));
    observer.observe(element);
    return () => {
      stop();
      observer.disconnect();
    };
  }, [ref, seed]);
}
